import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { CheckCircle2, Clock, FileText, Video, HelpCircle, Star, ExternalLink, BookOpen, FileQuestion, Info } from "lucide-react";
import { useState } from "react";
import { TaskHelpDialog } from "./TaskHelpDialog";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ALL_ACHIEVEMENTS } from "@/lib/achievementConfig"; // Achievement metadata for badge referencing
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";


interface Resource {
  type: 'document' | 'video' | 'link' | 'template' | 'help_link' | 'course' | 'quiz';
  title: string;
  url?: string;
  id?: string;
  description?: string;
  duration?: string;
}

interface SubTask {
  id: string;
  title: string;
  duration?: string;
  required?: boolean;
  milestone?: string;
  milestone_badge?: string;
  attachment?: {
    type: 'video' | 'document' | 'course' | 'template' | 'link' | 'quiz' | 'info' | 'workflow';
    url?: string;
    title: string;
  };
  attachments?: Array<{
    type: 'video' | 'document' | 'course' | 'template' | 'link' | 'quiz' | 'info' | 'workflow';
    url?: string;
    title: string;
  }>;
}


interface TaskDetail {
  id: string;
  task_number: number;
  task_title: string;
  task_description: string;
  category: string;
  phase: string;
  estimated_hours: number;
  is_first_step: boolean;
  first_step_number: number | null;
  prerequisites: number[];
  success_criteria: string[];
  resources: Resource[];
  sub_tasks?: SubTask[];
  workflow_id?: string | null;
  workflow_key?: string | null;
  is_workflow_task?: boolean;
  userProgress?: {
    status: string;
    notes: string | null;
    completed_at: string | null;
    sub_task_progress?: Record<string, boolean>;
  };
}

interface TaskNote {
  id: string;
  note_text: string;
  created_at: string;
}

interface TaskDetailModalProps {
  task: TaskDetail | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdateStatus: (taskId: string, status: string) => void;
  notesList?: TaskNote[];
  onAddNote?: (taskId: string, noteText: string) => Promise<void>;
  onUpdateSubTask?: (taskId: string, subTaskId: string, completed: boolean) => void;
}

const categoryLabels: Record<string, string> = {
  legal_foundation: "Legal Foundation",
  banking_treasury: "Banking & Treasury",
  governance: "Governance",
  asset_management: "Asset Management",
  operations: "Operations",
  education: "Education",
};

// Valid internal routes that exist in the app
const VALID_INTERNAL_ROUTES = [
  '/support',
  '/governance-documents',
  '/una-governance',
  '/una-members',
  '/my-tasks',
  '/workflows',
  '/journey',
  '/settings',
  '/dashboard',
  '/resources',
  '/analytics',
  '/automation',
  '/community',
];

// Helper to check if an internal URL is valid (won't 404)
const isValidInternalUrl = (url: string): boolean => {
  if (!url) return false;
  if (url.startsWith('http')) return true; // External URLs are always valid
  return VALID_INTERNAL_ROUTES.some(route => url.startsWith(route));
};

export const TaskDetailModal = ({ 
  task, 
  isOpen, 
  onClose, 
  onUpdateStatus,
  notesList = [],
  onAddNote,
  onUpdateSubTask
}: TaskDetailModalProps) => {
  const [newNote, setNewNote] = useState("");
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!task) return null;

  // Check for assignment status first (priority), fallback to userProgress
  const assignmentStatus = (task as any).assignment_status;
  const isCompleted = assignmentStatus === 'completed' || task.userProgress?.status === 'completed';
  const isInProgress = assignmentStatus === 'in_progress' || task.userProgress?.status === 'in_progress';
  
  // Check if all required sub-tasks are completed
  const allRequiredSubTasksComplete = !task.sub_tasks?.some(st => 
    st.required !== false && !task.userProgress?.sub_task_progress?.[st.id]
  );

  const handleAddNote = async () => {
    if (!newNote.trim() || !onAddNote) return;
    
    try {
      await onAddNote(task.id, newNote.trim());
      
      toast({
        title: "Note Added",
        description: "Your note has been saved successfully",
      });
      
      // Clear the input field after saving
      setNewNote("");
    } catch (error) {
      console.error('Error saving note:', error);
      toast({
        title: "Error",
        description: "Failed to save note",
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!user) return;
    
    try {
      // For MyAssignedTasks context, update the task_assignment status
      if ((task as any).assignment_id) {
        const updates: any = {
          status: newStatus,
        };
        
        if (newStatus === 'in_progress') {
          updates.started_at = new Date().toISOString();
        } else if (newStatus === 'completed') {
          updates.completed_at = new Date().toISOString();
        }
        
        const { error } = await supabase
          .from('task_assignments')
          .update(updates)
          .eq('id', (task as any).assignment_id);
        
        if (error) throw error;
        
        toast({
          title: newStatus === 'in_progress' ? "Task Started!" : "Status Updated",
          description: newStatus === 'in_progress' 
            ? "Good luck with your work!" 
            : `Task status changed to ${newStatus}`,
        });
        
        // Notify parent to refresh (this will update the modal's task prop)
        onUpdateStatus(task.id, newStatus);
        
        // Trigger confetti for completion
        if (newStatus === 'completed') {
          const duration = 3000;
          const animationEnd = Date.now() + duration;
          const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

          function randomInRange(min: number, max: number) {
            return Math.random() * (max - min) + min;
          }

          const interval: any = setInterval(function() {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
              return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);
            
            confetti({
              ...defaults,
              particleCount,
              origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
            });
            confetti({
              ...defaults,
              particleCount,
              origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
            });
          }, 250);

          toast({
            title: "🎉 Task Completed!",
            description: `Congratulations on completing "${task.task_title}"!`,
            duration: 5000,
          });
          
          // Close modal after completion
          onClose();
        }
        
        // Modal stays open for other status changes (e.g., start task)
      } else {
        // Regular task status update (non-assignment context)
        onUpdateStatus(task.id, newStatus);
        
        if (newStatus === 'completed') {
          // Existing confetti logic
          const duration = 3000;
          const animationEnd = Date.now() + duration;
          const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

          function randomInRange(min: number, max: number) {
            return Math.random() * (max - min) + min;
          }

          const interval: any = setInterval(function() {
            const timeLeft = animationEnd - Date.now();

            if (timeLeft <= 0) {
              return clearInterval(interval);
            }

            const particleCount = 50 * (timeLeft / duration);
            
            confetti({
              ...defaults,
              particleCount,
              origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
            });
            confetti({
              ...defaults,
              particleCount,
              origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
            });
          }, 250);

          toast({
            title: "🎉 Task Completed!",
            description: `Congratulations on completing "${task.task_title}"!`,
            duration: 5000,
          });
          
          onClose();
        }
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: "Error",
        description: "Failed to update task status",
        variant: "destructive",
      });
    }
  };

  const handleSubTaskToggle = async (subTaskId: string, completed: boolean) => {
    if (!onUpdateSubTask) return;
    
    const subTask = task.sub_tasks?.find(st => st.id === subTaskId);
    
    // Update the sub-task
    await onUpdateSubTask(task.id, subTaskId, completed);
    
    if (completed) {
      // Show milestone toast if present
      if (subTask?.milestone) {
        toast({
          title: subTask.milestone,
          description: "Great progress on your UNA journey!",
          duration: 3000,
        });
      }

      // If this step carries a milestone badge, award it immediately
      if (subTask?.milestone_badge && user?.id) {
        const ach = ALL_ACHIEVEMENTS.find(a => a.id === subTask.milestone_badge);
        if (ach) {
          try {
            await supabase.rpc('award_achievement', {
              p_user_id: user.id,
              p_achievement_id: ach.id,
              p_achievement_name: ach.name,
              p_description: ach.description,
            });
          } catch (e) {
            console.error('Failed to award achievement via RPC', e);
          }
        }
      }
    }
  };

  const getAttachmentIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="h-4 w-4 text-red-500" />;
      case 'document':
        return <FileText className="h-4 w-4 text-blue-500" />;
      case 'course':
        return <BookOpen className="h-4 w-4 text-purple-500" />;
      case 'template':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'quiz':
        return <FileQuestion className="h-4 w-4 text-orange-500" />;
      case 'link':
      default:
        return <ExternalLink className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'video':
        return <Video className="h-5 w-5 text-red-500" />;
      case 'document':
        return <FileText className="h-5 w-5 text-blue-500" />;
      case 'course':
        return <BookOpen className="h-5 w-5 text-purple-500" />;
      case 'template':
        return <FileText className="h-5 w-5 text-green-500" />;
      case 'help_link':
        return <HelpCircle className="h-5 w-5 text-orange-500" />;
      case 'link':
      default:
        return <ExternalLink className="h-5 w-5 text-muted-foreground" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Task #{task.task_number}</span>
              {task.is_first_step && (
                <Badge variant="secondary" className="gap-1">
                  <Star className="h-3 w-3 fill-current" />
                  First Step {task.first_step_number}
                </Badge>
              )}
              <Badge className="capitalize">{categoryLabels[task.category]}</Badge>
              <Badge variant="outline" className="capitalize">{task.phase} Phase</Badge>
            </div>
            <DialogTitle className="text-2xl">{task.task_title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Workflow Coming Soon Alert */}
          {task.is_workflow_task && !task.workflow_id && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertTitle>Workflow In Development</AlertTitle>
              <AlertDescription>
                This workflow is currently being built. Check back soon for the interactive guided experience!
              </AlertDescription>
            </Alert>
          )}

          {/* Description */}
          <div>
            <h3 className="font-semibold mb-2">Description</h3>
            <p className="text-muted-foreground">{task.task_description}</p>
          </div>

          <Separator />

          {/* Sub-Tasks */}
          {task.sub_tasks && task.sub_tasks.length > 0 && (
            <>
              <div>
                <h3 className="font-semibold mb-3">Steps to Complete</h3>
                <div className="space-y-3">
                  {task.sub_tasks.map((subTask) => {
                    const isSubTaskComplete = task.userProgress?.sub_task_progress?.[subTask.id] || false;
                    return (
                      <Card key={subTask.id} className={isSubTaskComplete ? 'bg-muted/50' : ''}>
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="min-h-[44px] min-w-[44px] flex items-center justify-center -ml-2">
                              <input
                                type="checkbox"
                                checked={isSubTaskComplete}
                                onChange={(e) => handleSubTaskToggle(subTask.id, e.target.checked)}
                                className="h-5 w-5 rounded border-gray-300 cursor-pointer"
                              />
                            </div>
                            <div className="flex-1 space-y-2">
                              <div className="flex items-start justify-between gap-2">
                                <span className={`text-sm font-medium ${isSubTaskComplete ? 'line-through text-muted-foreground' : ''}`}>
                                  {subTask.title}
                                </span>
                              </div>
                              
                              {/* Inline badge reference if this step awards one */}
                              {subTask.milestone_badge && (() => {
                                const ach = ALL_ACHIEVEMENTS.find(a => a.id === subTask.milestone_badge);
                                return ach ? (
                                  <div className="mt-1">
                                    <Badge variant="outline" className="text-[10px] gap-1">
                                      <span>{ach.name}</span>
                                    </Badge>
                                  </div>
                                ) : null;
                              })()}
                              
                              {/* Single attachment support - only show if URL is valid */}
                              {subTask.attachment && (
                                subTask.attachment.type === 'info' ? (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    ℹ️ {subTask.attachment.title}
                                  </p>
                                ) : subTask.attachment.url && isValidInternalUrl(subTask.attachment.url) ? (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-auto py-1 px-2 gap-2 text-xs"
                                    onClick={() => {
                                      if (subTask.attachment!.url!.startsWith('http')) {
                                        window.open(subTask.attachment!.url, '_blank');
                                      } else {
                                        navigate(subTask.attachment!.url!);
                                      }
                                    }}
                                  >
                                    {getAttachmentIcon(subTask.attachment.type)}
                                    <span>{subTask.attachment.title}</span>
                                  </Button>
                                ) : null
                              )}

                              {/* Multiple attachments support - filter to valid URLs only */}
                              {Array.isArray((subTask as any).attachments) && (subTask as any).attachments.filter((att: any) => att.url && isValidInternalUrl(att.url)).length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-1">
                                  {(subTask as any).attachments.filter((att: any) => att.url && isValidInternalUrl(att.url)).map((att: any, idx: number) => (
                                    <Button
                                      key={idx}
                                      variant="ghost"
                                      size="sm"
                                      className="h-auto py-1 px-2 gap-2 text-xs"
                                      onClick={() => {
                                        if (att.url?.startsWith('http')) {
                                          window.open(att.url, '_blank');
                                        } else if (att.url) {
                                          navigate(att.url);
                                        }
                                      }}
                                    >
                                      {getAttachmentIcon(att.type)}
                                      <span>{att.title}</span>
                                    </Button>
                                  ))}
                                </div>
                              )}
                              
                              {subTask.milestone && !isSubTaskComplete && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                                  {subTask.milestone}
                                </p>
                              )}

                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
              <Separator />
            </>
          )}

          <Separator />

          {/* Notes */}
          <div>
            <h3 className="font-semibold mb-3">Add a Note</h3>
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add notes about your progress..."
              rows={3}
              className="mb-2"
            />
            <Button onClick={handleAddNote} size="sm" disabled={!newNote.trim()}>
              Save Note
            </Button>

            {/* Saved Notes List */}
            {notesList.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold mb-3">Saved Notes</h3>
                <div className="space-y-3">
                  {notesList.map((note) => (
                    <Card key={note.id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <span className="text-xs text-muted-foreground">
                            {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.note_text}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex gap-2 justify-between">
            <Button 
              variant="outline" 
              className="gap-2"
              onClick={() => setHelpDialogOpen(true)}
            >
              <HelpCircle className="h-4 w-4" />
              Need Help?
            </Button>
            
            <div className="flex gap-2">
              {!isCompleted && !isInProgress && (
                <Button 
                  variant="secondary"
                  onClick={() => handleStatusChange('in_progress')}
                >
                  Start Task
                </Button>
              )}
              {isInProgress && (
                <Button
                  variant="default"
                  onClick={() => handleStatusChange('completed')}
                  className="gap-2"
                  disabled={!allRequiredSubTasksComplete}
                  title={!allRequiredSubTasksComplete ? 'Complete all required steps first' : ''}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Mark Complete
                </Button>
              )}
              {isCompleted && (
                <Button
                  variant="outline"
                  onClick={() => handleStatusChange('in_progress')}
                >
                  Mark Incomplete
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Help Dialog */}
        <TaskHelpDialog
          isOpen={helpDialogOpen}
          onClose={() => setHelpDialogOpen(false)}
          taskTitle={task.task_title}
          taskId={task.id}
          serviceOptions={[]}
        />
      </DialogContent>
    </Dialog>
  );
};
